import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useVehicleTracking } from '../../hooks/useVehicleTracking';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

// Importar los iconos SVG
import { ReactComponent as CarIcon } from '../../assets/svg/maps/Icon_tractor.svg';
import { ReactComponent as SpeedIcon } from '../../assets/svg/maps/speed.svg';
import { ReactComponent as BatteryIcon } from '../../assets/svg/components/battery.svg';
import { ReactComponent as TimeIcon } from '../../assets/svg/components/calendar.svg';
import { ReactComponent as LocationIcon } from '../../assets/svg/maps/location.svg';
import { ReactComponent as HistoryIcon } from '../../assets/svg/components/calendar.svg';
import { ReactComponent as CloseIcon } from '../../assets/svg/layout/close.svg';
import { ReactComponent as PhoneIcon } from '../../assets/svg/components/phone.svg';
import { ReactComponent as PersonIcon } from '../../assets/svg/layout/people.svg';
import { ReactComponent as ClipboardIcon } from '../../assets/svg/components/copy.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/svg/layout/panel_right.svg';

const formatRealTimeDate = (date) => {
  if (!date) return 'Fecha no disponible';
  
  try {
    // Para fechas en tiempo real del proveedor, usar tal como vienen
    const dateObj = new Date(date + (date.includes('Z') ? '' : 'Z'));
    
    return dateObj.toLocaleString('es-CO', {
      timeZone: 'America/Bogota', // Ajusta según tu zona horaria
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return 'Fecha inválida';
  }
};

const formatHistoryDate = (date) => {
  if (!date) return 'Fecha no disponible';
  
  try {
    // Para fechas de historial, usar comportamiento normal de JS
    const dateObj = new Date(date);
    return dateObj.toLocaleString('es-CO');
  } catch (error) {
    console.error('Error formateando fecha de historial:', error);
    return 'Fecha inválida';
  }
};

const VehicleDetails = ({ vehicle, onClose, theme }) => {
  const [startDate, setStartDate] = useState(new Date(Date.now() - 86400000).toISOString().split('T')[0]); // Ayer
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]); // Hoy
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  
  const { getVehicleHistory } = useVehicleTracking();
  const { copyToClipboard } = useCopyToClipboard();
  
  // Función para copiar coordenadas
  const handleCopyCoordinates = useCallback(() => {
    if (!vehicle) return;
    
    const lat = parseFloat(vehicle.Latitude);
    const lng = parseFloat(vehicle.Longitude);
    
    if (!isNaN(lat) && !isNaN(lng)) {
      const coordsText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      copyToClipboard(coordsText);
      setCopySuccess('Coordenadas copiadas');
      
      setTimeout(() => {
        setCopySuccess('');
      }, 2000);
    }
  }, [vehicle, copyToClipboard]);
  
  // Función para copiar ubicación (dirección)
  const handleCopyLocation = useCallback(() => {
    if (!vehicle || !vehicle.Domicilio) return;
    
    copyToClipboard(vehicle.Domicilio);
    setCopySuccess('Ubicación copiada');
    
    setTimeout(() => {
      setCopySuccess('');
    }, 2000);
  }, [vehicle, copyToClipboard]);
  
  const handleGetHistory = useCallback(async () => {
    if (!vehicle) return;
    
    try {
      setIsLoadingHistory(true);
      setShowHistory(true);
      
      const vehicleId = vehicle.UnitId || vehicle.UnitPlate || vehicle.GpsIdentif;
      const formattedStartDate = `${startDate} 00:00:00`;
      const formattedEndDate = `${endDate} 23:59:59`;
      
      const result = await getVehicleHistory(
        vehicleId, 
        formattedStartDate, 
        formattedEndDate
      );
      
      setHistoryData(result.data);
      setIsHistoryExpanded(true);
    } catch (error) {
      console.error('Error obteniendo historial:', error);
      setHistoryData([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [vehicle, startDate, endDate, getVehicleHistory]);
  
  // Si no hay vehículo seleccionado
  if (!vehicle) {
    return (
      <div className={`GM__${theme}-empty-details`}>
        <h2 className={`GM__${theme}-empty-details-title`}>Seleccione un vehículo para ver detalles</h2>
      </div>
    );
  }
  
  // Determinar estado del vehículo
  const isMoving = (vehicle.GpsSpeed || 0) > 0;
  const isIgnitionOn = vehicle.Ignition === 1;
  const batteryLevel = vehicle.BateriaGps || 'N/A';
  
  return (
    <div className={`GM__${theme}-vehicle-details-container`}>
      <div className={`GM__${theme}-details-header`}>
        <h2 className={`GM__${theme}-details-title`}>Detalles del Vehículo</h2>
        <button 
          onClick={onClose} 
          className={`GM__${theme}-close-button`}
          aria-label="cerrar"
        >
          <CloseIcon className={`GM__${theme}-close-icon`} />
        </button>
      </div>
      
      <div className={`GM__${theme}-details-card`}>
        <div className={`GM__${theme}-vehicle-header`}>
          <CarIcon className={`GM__${theme}-vehicle-icon-large`} />
          <div className={`GM__${theme}-vehicle-title`}>
            <h3 className={`GM__${theme}-vehicle-name`}>{vehicle.UnitId || 'Vehículo'}</h3>
            <p className={`GM__${theme}-vehicle-plate`}>Placa: {vehicle.UnitPlate || 'N/A'}</p>
          </div>
        </div>
        
        <div className={`GM__${theme}-details-divider`}></div>
        
        <div className={`GM__${theme}-details-grid`}>
          <div className={`GM__${theme}-detail-item`}>
            <SpeedIcon className={`GM__${theme}-detail-icon`} />
            <p className={`GM__${theme}-detail-text`}>
              <strong>Velocidad:</strong> {vehicle.GpsSpeed || 0} km/h
            </p>
          </div>
          
          <div className={`GM__${theme}-detail-item`}>
            <BatteryIcon className={`GM__${theme}-detail-icon`} />
            <p className={`GM__${theme}-detail-text`}>
              <strong>Batería:</strong> {batteryLevel}
            </p>
          </div>
          
          <div className={`GM__${theme}-detail-item full-width`}>
            <div className={`GM__${theme}-location-container`}>
              <LocationIcon className={`GM__${theme}-detail-icon`} />
              <p className={`GM__${theme}-detail-text`}>
                <strong>Ubicación:</strong> {vehicle.Domicilio || 'No disponible'}
              </p>
              {vehicle.Domicilio && (
                <button 
                  onClick={handleCopyLocation}
                  className={`GM__${theme}-copy-button`}
                  title="Copiar ubicación"
                >
                  <ClipboardIcon className={`GM__${theme}-icon-small`} />
                </button>
              )}
            </div>
          </div>
          
          <div className={`GM__${theme}-detail-item full-width`}>
            <div className={`GM__${theme}-location-container`}>
              <LocationIcon className={`GM__${theme}-detail-icon`} />
              <p className={`GM__${theme}-detail-text`}>
                <strong>Coordenadas:</strong> {parseFloat(vehicle.Latitude).toFixed(6)}, {parseFloat(vehicle.Longitude).toFixed(6)}
              </p>
              <button 
                onClick={handleCopyCoordinates}
                className={`GM__${theme}-copy-button`}
                title="Copiar coordenadas"
              >
                <ClipboardIcon className={`GM__${theme}-icon-small`} />
              </button>
            </div>
            {copySuccess && (
              <div className={`GM__${theme}-copy-success`}>
                {copySuccess}
              </div>
            )}
          </div>
          
          <div className={`GM__${theme}-detail-item`}>
            <TimeIcon className={`GM__${theme}-detail-icon`} />
            <p className={`GM__${theme}-detail-text`}>
              <strong>Último reporte:</strong> {formatRealTimeDate(vehicle.ReportDate)}
            </p>
          </div>
          
          <div className={`GM__${theme}-status-badge-container`}>
            <div className={`GM__${theme}-status-badge ${isMoving ? 'moving' : (isIgnitionOn ? 'idle' : 'parked')}`}>
              {isMoving ? 'En movimiento' : (isIgnitionOn ? 'Motor encendido' : 'Estacionado')}
            </div>
          </div>
        </div>
      </div>
      
      {vehicle.Conductor && (
        <div className={`GM__${theme}-details-card`}>
          <div className={`GM__${theme}-card-header`}>
            <PersonIcon className={`GM__${theme}-card-icon`} />
            <h3 className={`GM__${theme}-card-title`}>Información del Conductor</h3>
          </div>
          
          <p className={`GM__${theme}-conductor-info`}>
            <strong>Nombre:</strong> {vehicle.Conductor}
          </p>
          
          {vehicle.IdConductor && (
            <p className={`GM__${theme}-conductor-info`}>
              <strong>ID:</strong> {vehicle.IdConductor}
            </p>
          )}
          
          {/* Botón de llamada si hubiera número de teléfono */}
          {vehicle.PhoneDriver && (
            <a 
              className={`GM__${theme}-phone-button`}
              href={`tel:${vehicle.PhoneDriver}`}
            >
              <PhoneIcon className={`GM__${theme}-phone-icon`} />
              <span>Llamar</span>
            </a>
          )}
        </div>
      )}
      
      <div className={`GM__${theme}-accordion`}>
        <div 
          className={`GM__${theme}-accordion-header`}
          onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
        >
          <div className={`GM__${theme}-accordion-title`}>
            <HistoryIcon className={`GM__${theme}-accordion-icon`} />
            <h3>Historial de Recorrido</h3>
          </div>
          <ChevronDownIcon className={`GM__${theme}-chevron-icon ${isHistoryExpanded ? 'expanded' : ''}`} />
        </div>
        
        {isHistoryExpanded && (
          <div className={`GM__${theme}-accordion-content`}>
            <div className={`GM__${theme}-history-form`}>
              <div className={`GM__${theme}-date-inputs`}>
                <div className={`GM__${theme}-form-group`}>
                  <label className={`GM__${theme}-form-label`}>Fecha Inicial</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`GM__${theme}-date-input`}
                  />
                </div>
                
                <div className={`GM__${theme}-form-group`}>
                  <label className={`GM__${theme}-form-label`}>Fecha Final</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`GM__${theme}-date-input`}
                  />
                </div>
              </div>
              
              <button
                className={`GM__${theme}-history-button`}
                onClick={handleGetHistory}
                disabled={isLoadingHistory}
              >
                {isLoadingHistory ? (
                  <>
                    <div className={`GM__${theme}-loading-spinner-small`}></div>
                    <span>Cargando...</span>
                  </>
                ) : (
                  <>
                    <HistoryIcon className={`GM__${theme}-button-icon`} />
                    <span>Ver Historial</span>
                  </>
                )}
              </button>
              
              {showHistory && (
                <div className={`GM__${theme}-history-results`}>
                  {isLoadingHistory ? (
                    <div className={`GM__${theme}-loading-container`}>
                      <div className={`GM__${theme}-loading-spinner`}></div>
                    </div>
                  ) : (
                    <>
                      {historyData && historyData.length > 0 ? (
                        <div className={`GM__${theme}-history-list`}>
                          <p className={`GM__${theme}-history-count`}>
                            {historyData.length} registros encontrados
                          </p>
                          
                          {historyData.map((record, index) => (
                            <div key={index} className={`GM__${theme}-history-item`}>
                              <div className={`GM__${theme}-history-grid`}>
                                <div className={`GM__${theme}-history-cell`}>
                                  <p className={`GM__${theme}-history-label`}>Fecha:</p>
                                  <p className={`GM__${theme}-history-value`}>
                                    {formatHistoryDate(record.fecha_reporte || record.ReportDate)}
                                  </p>
                                </div>
                                
                                <div className={`GM__${theme}-history-cell`}>
                                  <p className={`GM__${theme}-history-label`}>Velocidad:</p>
                                  <p className={`GM__${theme}-history-value`}>
                                    {record.velocidad || record.GpsSpeed || 0} km/h
                                  </p>
                                </div>
                                
                                <div className={`GM__${theme}-history-cell full-width`}>
                                  <p className={`GM__${theme}-history-label`}>Ubicación:</p>
                                  <p className={`GM__${theme}-history-value location`}>
                                    {record.domicilio || record.Domicilio || 'No disponible'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={`GM__${theme}-no-history`}>
                          No se encontraron registros en el período seleccionado
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

VehicleDetails.propTypes = {
  vehicle: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired
};

export default VehicleDetails;